import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, LoginCredentials, RegisterData, User, SupportMessage } from '../types/auth';
import { authService } from '../services/authService';
import { dbService } from '../services/dbService';
import { doc, onSnapshot, getDoc, runTransaction, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      loading: false,
      error: null,

      login: async (credentials: LoginCredentials) => {
        set({ loading: true, error: null });
        try {
          const userData = await authService.login(credentials.email, credentials.password);
          set({
            user: userData,
            isAuthenticated: true,
            loading: false
          });
        } catch (error) {
          set({ error: 'Giriş başarısız', loading: false });
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        set({ loading: true, error: null });
        try {
          const userData = await authService.register(data.email, data.password, data.name, data.accountType);
          set({
            user: userData,
            isAuthenticated: true,
            loading: false
          });
        } catch (error) {
          set({ error: 'Kayıt başarısız', loading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await authService.logout();
          set({
            user: null,
            isAuthenticated: false,
            error: null
          });
          // Tüm state'i temizle
          localStorage.removeItem('auth-store');
          localStorage.removeItem('trading-store');
          window.location.href = '/login'; // Sayfayı yenile ve login'e yönlendir
        } catch (error) {
          console.error('Logout error:', error);
          throw error;
        }
      },

      getAllUsers: async () => {
        try {
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('isAdmin', '==', false));
          const snapshot = await getDocs(q);
          
          return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            balance: doc.data().balance || 0,
            verificationStatus: doc.data().verificationStatus || 'unverified'
          })) as User[];
        } catch (error) {
          console.error('Get users error:', error);
          throw error;
        }
      },

      updateUserBalance: async (userId: string, amount: number) => {
        try {
          await runTransaction(db, async (transaction) => {
            const userRef = doc(db, 'users', userId);
            const userDoc = await transaction.get(userRef);
            
            if (!userDoc.exists()) {
              throw new Error('Kullanıcı bulunamadı');
            }

            transaction.update(userRef, {
              balance: amount
            });

            const state = get();
            if (state.user?.id === userId) {
              set({
                user: {
                  ...state.user,
                  balance: amount
                }
              });
            }
          });

          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            return {
              id: userDoc.id,
              ...userDoc.data(),
              balance: userDoc.data().balance || 0
            } as User;
          }
        } catch (error) {
          console.error('Update balance error:', error);
          throw error;
        }
      },

      updateUserVerificationStatus: async (userId: string, status: 'verified' | 'rejected') => {
        try {
          await dbService.updateUser(userId, { verificationStatus: status });
          const updatedUser = await dbService.getUserById(userId);
          return updatedUser;
        } catch (error) {
          console.error('Update verification status error:', error);
          throw error;
        }
      },

      addSupportMessage: async (message: SupportMessage) => {
        try {
          await dbService.createSupportMessage(message);
        } catch (error) {
          console.error('Add support message error:', error);
          throw error;
        }
      },

      refreshUserData: async () => {
        const state = get();
        if (state.user?.id) {
          try {
            const userRef = doc(db, 'users', state.user.id);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const updatedUser = {
                id: userDoc.id,
                ...userDoc.data(),
                balance: userDoc.data().balance || 0
              } as User;
              set({ user: updatedUser });
            }
          } catch (error) {
            console.error('Refresh user data error:', error);
            throw error;
          }
        }
      }
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);